import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Helmet } from "@/components/Helmet";

const LAST_UPDATED = "2026-04-09";

const SUBPROCESSORS = [
  {
    name: "Supabase",
    purpose: "Auth, database, edge functions",
    dataCategory: "Account data, service data",
  },
  {
    name: "Sentry",
    purpose: "Error monitoring/logging",
    dataCategory: "Usage metadata, diagnostic logs",
  },
  {
    name: "Have I Been Pwned Passwords API",
    purpose: "Optional WordPress plugin breached-password checks",
    dataCategory: "5-character SHA-1 hash prefixes",
  },
  {
    name: "PostHog",
    purpose: "Product analytics",
    dataCategory: "Usage metadata",
  },
  {
    name: "Matomo",
    purpose: "Web analytics",
    dataCategory: "Usage metadata",
  },
  {
    name: "Stripe",
    purpose: "Payments and subscriptions",
    dataCategory: "Billing details",
  },
  {
    name: "Resend",
    purpose: "Email delivery",
    dataCategory: "Email metadata",
  },
  {
    name: "Cloudflare Turnstile",
    purpose: "Bot protection",
    dataCategory: "Abuse-prevention signals",
  },
  {
    name: "Rewardful",
    purpose: "Affiliate attribution after analytics consent",
    dataCategory: "Referral identifiers, campaign metadata",
  },
  {
    name: "AI processing provider",
    purpose: "AI-assisted compliance, remediation, exploit-guide, and support guidance",
    dataCategory: "Prompt content, scan context, site metadata",
  },
];

const Subprocessors = () => {
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Helmet>
        <title>Subprocessors | ShadowScan</title>
        <meta
          name="description"
          content="ShadowScan subprocessor inventory and service provider list."
        />
      </Helmet>
      <Navigation />
      <main className="flex-grow pt-24">
        <section className="relative py-16 overflow-hidden">
          <div className="absolute inset-0 bg-grid-pattern opacity-20 -z-10" />
          <div className="absolute inset-0 bg-gradient-to-b from-primary/10 via-transparent to-transparent -z-10" />
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary/30 bg-primary/5 mb-6">
                <span className="font-mono text-sm text-primary">// SUBPROCESSORS</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-heading font-bold mb-3">Subprocessors</h1>
              <p className="text-muted-foreground">Last updated: {LAST_UPDATED}</p>
            </div>
          </div>
        </section>

        <section className="py-12">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto cyber-card rounded-xl p-8 border-primary/30 space-y-6">
              <p className="text-muted-foreground">
                The following subprocessors may process limited data on behalf of ShadowScan in order to provide our services. We review this list regularly and update it as our services evolve.
              </p>

              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm text-muted-foreground">
                  <thead className="border-b border-border/60 text-xs uppercase tracking-wider text-muted-foreground">
                    <tr>
                      <th className="py-3 pr-6">Subprocessor</th>
                      <th className="py-3 pr-6">Purpose of processing</th>
                      <th className="py-3">Data category</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/40">
                    {SUBPROCESSORS.map((item) => (
                      <tr key={item.name}>
                        <td className="py-4 pr-6 font-medium text-foreground whitespace-nowrap">{item.name}</td>
                        <td className="py-4 pr-6">{item.purpose}</td>
                        <td className="py-4">{item.dataCategory}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <p className="text-sm text-muted-foreground">
                Additional details regarding our subprocessors may be provided under NDA upon request.
              </p>
              <p className="text-sm text-muted-foreground">
                Where a processor is described generically, the relevant provider may change over time and this page will be updated when the public inventory changes.
              </p>
              <p className="text-sm text-muted-foreground">
                This list reflects subprocessors used for ShadowScan service delivery, checkout support, and consented affiliate attribution. Purely internal tools that do not process customer or prospect data on our behalf are excluded.
              </p>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Subprocessors;
